import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Play } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Modal } from "@/components/Modal";
import { VideoEmbed } from "@/components/VideoEmbed";

const FILTERS = ["All", "Music", "Entertainment", "Sports", "Media", "Leaders & Public Figures"];

function initials(name) {
  return name
    .replace(/&.*$/, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function GuestPhoto({ guest, className = "" }) {
  const [err, setErr] = useState(false);
  const src = guest.photo_clean || guest.photo;
  if (!src || err) {
    return (
      <div className={`${className} bg-gradient-to-br from-cyan-100 via-cyan-50 to-pool-light/60 flex flex-col items-center justify-center`} data-testid={`guest-placeholder-${guest.id}`}>
        <span className="font-script text-5xl text-pool/70 leading-none">{initials(guest.name)}</span>
        <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-pool-deep/50 mt-3">Photo coming soon</span>
      </div>
    );
  }
  return (
    <img
      src={imgSrc(src)}
      alt={guest.name}
      loading="lazy"
      onError={() => setErr(true)}
      style={{ objectPosition: guest.photo_clean ? "center 18%" : guest.focus || "center 25%" }}
      className={`${className} guest-img img-zoom object-cover`}
    />
  );
}

function GuestCard({ guest, index, feature = false, onPlay }) {
  return (
    <Reveal delay={(index % 4) * 0.06} className="h-full">
      <Link
        to={`/spotlight/${guest.id}`}
        className="group flex flex-col h-full bg-white rounded-xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-[0_18px_45px_rgba(2,20,30,0.12)] hover:-translate-y-1 transition-all duration-500"
        data-testid={`spotlight-guest-${guest.id}`}
      >
        <div className="relative aspect-[3/4] overflow-hidden bg-[#f6f1ea]">
          <GuestPhoto guest={guest} className="absolute inset-0 h-full w-full" />
          {feature && (
            <span className="absolute top-3 left-3 bg-pool text-white text-[9px] tracking-[0.22em] uppercase font-bold px-3 py-1.5 rounded-full shadow-lg">
              Featured
            </span>
          )}
        </div>
        <div className={`flex flex-col flex-1 items-center text-center ${feature ? "p-5" : "p-4 sm:p-5"}`} data-testid={`spotlight-card-text-${guest.id}`}>
          <h3 className={`font-display font-black text-ink leading-tight tracking-[0.06em] uppercase ${feature ? "text-lg" : "text-sm sm:text-base"}`}>
            {guest.name}
          </h3>
          {guest.category && (
            <p className="text-[9px] tracking-[0.28em] uppercase font-bold text-pool mt-1.5">{guest.category}</p>
          )}
          {guest.appearance_link && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlay?.(guest);
              }}
              data-testid={`spotlight-watch-${guest.id}`}
              className="mt-4 inline-flex items-center gap-2 bg-pool hover:bg-pool-dark text-white text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2.5 rounded-full transition-colors"
            >
              <Play className="h-3.5 w-3.5" /> Watch Feature
            </button>
          )}
        </div>
      </Link>
    </Reveal>
  );
}

export default function Spotlight() {
  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState("All");
  const [playingGuest, setPlayingGuest] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/content/guests").then((r) => setGuests(r.data)).catch(() => {});
  }, []);

  const showGuests = guests.filter((g) => g.group !== "VIP & Special Guest");
  const vips = guests.filter((g) => g.group === "VIP & Special Guest");
  const directory = filter === "All" ? showGuests : showGuests.filter((g) => g.category === filter);

  return (
    <div data-testid="spotlight-page" className="bg-white">
      {/* HERO — short, text-led water brand moment */}
      <section className="relative overflow-hidden" data-testid="spotlight-intro">
        <img src="/assets/shop-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.62),rgba(255,255,255,0.22)_58%,rgba(255,255,255,0.06)_82%)] pointer-events-none" aria-hidden="true" />
        <div className="relative container-site py-16 sm:py-20 lg:py-24 text-center max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="label-eyebrow mb-5" data-testid="spotlight-eyebrow">Poolside Cooking Presents</p>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-ink" data-testid="spotlight-headline">
              Faces at<br /><span className="text-pool">the Pool.</span>
            </h1>
            <p className="mt-6 font-hero text-lg sm:text-xl text-ink/80 max-w-xl mx-auto" data-testid="spotlight-subcopy">
              From legendary names to unforgettable personalities, every guest brings a story to the table.
            </p>
            <a
              href="#guest-directory"
              data-testid="spotlight-meet-guests"
              className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-bold text-pool-deep/70 hover:text-pool transition-colors"
            >
              Meet the Guests <ArrowDown className="h-3.5 w-3.5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* GUEST DIRECTORY */}
      <section id="guest-directory" className="py-14 sm:py-16 scroll-mt-24" data-testid="spotlight-grid-section">
        <div className="container-site">
          <Reveal className="mb-8">
            <p className="label-eyebrow mb-3">Guest Directory</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink">
              Everyone Who's <span className="text-pool">Pulled Up a Chair.</span>
            </h2>
          </Reveal>

          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="guest-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[10px] sm:text-xs tracking-[0.15em] uppercase font-bold px-5 py-2.5 rounded-full border transition-colors ${
                  filter === f
                    ? "bg-pool text-white border-pool"
                    : "bg-white text-slate-500 border-slate-200 hover:border-pool hover:text-pool"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6" data-testid="spotlight-grid">
            {(showAll ? directory : directory.slice(0, 12)).map((g, i) => (
              <GuestCard key={g.id} guest={g} index={i} onPlay={setPlayingGuest} />
            ))}
          </div>
          {!showAll && directory.length > 12 && (
            <div className="mt-10 text-center">
              <button
                onClick={() => setShowAll(true)}
                data-testid="view-more-guests"
                className="inline-flex items-center gap-2.5 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xs tracking-[0.18em] uppercase px-8 py-4 rounded-full transition-colors"
              >
                View More Guests ↓
              </button>
            </div>
          )}
          {directory.length === 0 && (
            <p className="text-slate-500 text-sm py-16 text-center" data-testid="spotlight-empty">
              No guests in this category yet.
            </p>
          )}
        </div>
      </section>

      {/* POOLSIDE VIPS & SPECIAL GUESTS */}
      {vips.length > 0 && (
        <section className="py-14 sm:py-16 bg-cream border-y border-amber-200/60" data-testid="vip-section">
          <div className="container-site">
            <Reveal className="max-w-2xl mb-12">
              <p className="text-xs tracking-[0.3em] uppercase font-semibold text-amber-500 mb-3">Beyond the Show</p>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink" data-testid="vip-heading">
                Poolside VIPs <span className="text-pool">&amp; Special Guests</span>
              </h2>
              <p className="mt-4 text-slate-600 text-sm sm:text-base leading-relaxed" data-testid="vip-subtitle">
                Notable faces we've welcomed at the Poolside Cooking Café, Celebrity Brunch and special events.
              </p>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="vip-grid">
              {vips.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08}>
                  <div
                    className="group grid sm:grid-cols-[42%_58%] h-full bg-white rounded-xl overflow-hidden shadow-sm border border-amber-200/70 hover:shadow-[0_20px_55px_rgba(2,20,30,0.15)] transition-all duration-500"
                    data-testid={`vip-card-${v.id}`}
                  >
                    <div className="relative aspect-[4/5] sm:aspect-auto sm:min-h-[280px] overflow-hidden bg-[#f6f1ea]">
                      <GuestPhoto guest={v} className="absolute inset-0 h-full w-full" />
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col justify-center">
                      {v.designation && (
                        <span
                          className="self-start text-[9px] tracking-[0.25em] uppercase font-bold text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full"
                          data-testid={`vip-label-${v.id}`}
                        >
                          {v.designation}
                        </span>
                      )}
                      <h3 className="mt-4 font-display font-black text-xl sm:text-2xl text-ink leading-tight">{v.name}</h3>
                      {v.descriptor && <p className="text-pool-deep font-semibold text-xs mt-1.5">{v.descriptor}</p>}
                      {v.bio && <p className="text-slate-500 text-xs sm:text-sm leading-relaxed mt-3">{v.bio}</p>}
                      {v.appearance_note && (
                        <p className="mt-4 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">{v.appearance_note}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL BRAND MESSAGE */}
      <section className="water-panel text-white noise-overlay" data-testid="spotlight-cta">
        <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-site py-14 sm:py-16 text-center max-w-3xl mx-auto">
          <Reveal>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="spotlight-final-headline">
              Every Guest Brings<br />a <span className="text-pool-light">Story to the Table.</span>
            </h2>
            <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
              Poolside Cooking is where food, entertainment, culture and conversation come together.
            </p>
            <Link
              to="/watch"
              data-testid="spotlight-watch-cta"
              className="mt-9 inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
            >
              Watch Poolside Cooking <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GUEST VIDEO MODAL — 16:9 preserved */}
      <Modal open={!!playingGuest} onClose={() => setPlayingGuest(null)} testId="guest-video-modal">
        {playingGuest && (
          <div className="bg-ink rounded-2xl overflow-hidden shadow-2xl">
            {/\.(mp4|mov|webm)(\?|$)/i.test(playingGuest.appearance_link || "") ? (
              <video
                src={imgSrc(playingGuest.appearance_link)}
                controls
                autoPlay
                playsInline
                className="w-full aspect-video bg-black"
                data-testid="guest-video-player"
              />
            ) : (
              <VideoEmbed url={playingGuest.appearance_link} title={playingGuest.name} />
            )}
            <div className="p-6 sm:p-8 text-white">
              <p className="font-display font-black text-xl sm:text-2xl tracking-tight" data-testid="guest-video-title">
                {playingGuest.name} <span className="text-pool">× Poolside Cooking</span>
              </p>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">
                Watch a moment from {playingGuest.name}'s appearance on Poolside Cooking.
              </p>
              {playingGuest.episode_link && (
                <a
                  href={playingGuest.episode_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="guest-full-episode-cta"
                  className="mt-6 inline-flex items-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-6 py-3.5 rounded-full transition-colors"
                >
                  Watch Full Episode <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
